import { useState } from 'react'
import type { HeroLabels } from '@/pages/landing/types'

function KegGlyph({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path d="M4 2.5h8l-.6 10.2a1.5 1.5 0 0 1-1.5 1.4H6.1a1.5 1.5 0 0 1-1.5-1.4L4 2.5Z" fill="#FBF4E4" />
      <path d="M4.2 6.2h7.6" stroke="#E2461F" strokeWidth="1.5" />
    </svg>
  )
}

const them = 'self-start max-w-[85%] px-2.5 py-1.5 rounded-2xl rounded-bl-sm text-[0.72rem] leading-snug bg-white border border-[#d8cfb8]'
const me = 'self-end max-w-[85%] px-2.5 py-1.5 rounded-2xl rounded-br-sm text-[0.72rem] leading-snug bg-[#DCF8C6] border border-[#bfe6a6]'

function BeforeScreen({ labels }: { labels: HeroLabels['before'] }) {
  return (
    <>
      <div className="bg-[#0B6B5E] text-white px-4 pt-7 pb-2.5 flex items-center gap-2.5">
        <span className="text-lg leading-none">‹</span>
        <span className="w-7 h-7 rounded-full bg-white/25 grid place-items-center text-xs">👥</span>
        <div className="leading-tight">
          <div className="font-bold text-[0.82rem]">{labels.app_name}</div>
          <div className="text-[0.58rem] opacity-80">{labels.members}</div>
        </div>
      </div>
      <div className="flex-1 overflow-hidden px-3 py-3 bg-[#ECE5D3] flex flex-col gap-1.5">
        {labels.messages.map((m, i) => (
          <div key={i} className={m.side === 'me' ? me : them}>{m.text}</div>
        ))}
      </div>
      <div className="px-3 py-2 bg-[#ECE5D3] flex items-center gap-2">
        <div className="flex-1 bg-white rounded-full px-3 py-1.5 text-[0.68rem] text-ink-faint border border-[#d8cfb8]">{labels.input}</div>
        <div className="w-7 h-7 rounded-full bg-[#0B6B5E] grid place-items-center text-white text-xs">➤</div>
      </div>
    </>
  )
}

const pill = 'text-[0.64rem] font-extrabold px-2.5 py-1 rounded-full whitespace-nowrap shrink-0'
const clientRow = 'flex items-center justify-between gap-2 py-3.5 border-b border-line'
const peekRow = 'flex items-center justify-between gap-2 py-3.5 [-webkit-mask-image:linear-gradient(to_bottom,#000_35%,transparent_95%)] [mask-image:linear-gradient(to_bottom,#000_35%,transparent_95%)]'

function metaToneClass(tone: HeroLabels['after']['clients'][number]['meta_tone']) {
  if (tone === 'good') return 'text-leaf-deep font-semibold'
  if (tone === 'bad') return 'text-flame-deep font-semibold'
  return 'text-ink-faint'
}

function badgeToneClass(tone: HeroLabels['after']['clients'][number]['badge_tone']) {
  if (tone === 'ok') return 'bg-[#CDE9BF] text-leaf-deep'
  if (tone === 'warn') return 'bg-flame text-paper'
  return 'bg-paper-2 text-ink-soft'
}

function AfterScreen({ labels }: { labels: HeroLabels['after'] }) {
  return (
    <>
      <div className="bg-card border-b border-line px-4 pt-7 pb-2.5 flex items-center gap-2">
        <span className="w-6 h-6 bg-ink rounded-md grid place-items-center"><KegGlyph /></span>
        <span className="font-extrabold text-[0.85rem] text-ink">{labels.brand}</span>
        <span className="ml-auto text-[0.6rem] font-extrabold text-flame">{labels.live}</span>
      </div>
      <div className="flex-1 overflow-hidden px-4 pt-5 pb-3 flex flex-col gap-6">
        <div className="flex items-baseline justify-between">
          <span className="font-extrabold text-base text-ink">{labels.title}</span>
          <span className="text-[0.7rem] font-semibold text-ink-faint">{labels.count}</span>
        </div>

        <div className="flex items-center gap-2 text-[0.68rem] font-semibold text-leaf-deep">
          <span className="w-2 h-2 rounded-full bg-leaf shrink-0" />
          {labels.located}
        </div>

        <div>
          <div className="text-[0.58rem] font-bold uppercase tracking-[0.12em] text-ink-faint mb-2.5">{labels.warehouse_label}</div>
          <div className="flex gap-4 text-[0.75rem] font-semibold text-ink">
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-leaf" />{labels.warehouse_clean}</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-mustard" />{labels.warehouse_wash}</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-flame" />{labels.warehouse_repair}</span>
          </div>
        </div>

        <div>
          <div className="text-[0.58rem] font-bold uppercase tracking-[0.12em] text-ink-faint mb-1">{labels.clients_label}</div>

          {labels.clients.map((c, i) => {
            const isLast = i === labels.clients.length - 1
            return (
              <div key={c.name} className={isLast ? peekRow : clientRow}>
                <div>
                  <div className="font-bold text-[0.82rem] text-ink leading-tight">{c.name}</div>
                  <div className="text-[0.66rem] text-ink-faint mt-0.5">
                    {c.meta_pre}
                    {c.meta_accent && <span className={metaToneClass(c.meta_tone)}>{c.meta_accent}</span>}
                  </div>
                </div>
                <span className={`${pill} ${badgeToneClass(c.badge_tone)}`}>{c.badge}</span>
              </div>
            )
          })}
        </div>
      </div>
      <div className="p-3">
        <button className="w-full bg-flame text-paper rounded-2xl py-3 font-bold text-[0.85rem] flex items-center justify-center gap-2">
          <span className="text-base leading-none">＋</span> {labels.scan}
        </button>
      </div>
    </>
  )
}

export default function Hero({ labels, microcopy }: { labels: HeroLabels; microcopy: string }) {
  const [view, setView] = useState<'before' | 'after'>('before')

  return (
    <section className="pt-28 pb-16 lg:pt-32 lg:pb-24 px-[22px]">
      <div className="max-w-[1080px] mx-auto grid lg:grid-cols-[1.02fr_0.98fr] gap-10 lg:gap-11 items-center">
        <div>
          <h1 className="font-body font-extrabold text-ink text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-2 animate-fade-in-up">
            <span className="font-script font-normal text-flame text-[1.08em]">{labels.title_zero}</span> {labels.title_rest}
          </h1>

          <p className="font-body text-lg md:text-xl text-ink-soft max-w-[36ch] mt-4 mb-7 animate-fade-in-up animation-delay-100">
            {labels.lead_pre}{' '}
            <span className="sketch">{labels.lead_highlight}</span>{' '}
            {labels.lead_post}
          </p>

          <div className="flex flex-wrap items-center gap-4 animate-fade-in-up animation-delay-200">
            <a href="#waitlist" className="inline-flex items-center gap-2 font-bold text-paper bg-flame hover:bg-flame-deep border-2 border-ink rounded-[14px] px-6 py-3.5 text-lg shadow-hard hover:-translate-y-0.5 transition-all">
              {labels.cta_primary} <span>→</span>
            </a>
            <a href="#calculator" className="inline-flex items-center gap-2 font-bold text-ink bg-card border-2 border-ink rounded-[14px] px-6 py-3.5 shadow-[3px_3px_0_#2A1608] hover:-translate-y-0.5 transition-all">
              {labels.cta_secondary}
            </a>
          </div>

          <p className="mt-4 text-sm text-ink-soft font-semibold animate-fade-in-up animation-delay-300">
            {microcopy}
          </p>
        </div>

        {/* Mockup smartphone avec bascule Avant / Après */}
        <div className="animate-fade-in-up animation-delay-400">
          <div className="flex justify-center mb-5">
            <div className="inline-flex items-center gap-1 border-2 border-ink rounded-full p-1 bg-card shadow-[3px_3px_0_#2A1608]">
              <button
                type="button"
                onClick={() => setView('before')}
                aria-pressed={view === 'before'}
                className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${view === 'before' ? 'bg-ink text-paper' : 'text-ink-soft hover:text-ink'}`}
              >
                {labels.toggle_before}
              </button>
              <button
                type="button"
                onClick={() => setView('after')}
                aria-pressed={view === 'after'}
                className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${view === 'after' ? 'bg-flame text-paper' : 'text-ink-soft hover:text-ink'}`}
              >
                {labels.toggle_after}
              </button>
            </div>
          </div>

          <div className="relative mx-auto w-[280px]">
            <div className="rounded-[2.6rem] bg-ink p-2.5 shadow-hard">
              <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-28 h-6 bg-ink rounded-b-2xl z-10" />
              <div className="rounded-[2.1rem] bg-paper overflow-hidden h-[520px] flex flex-col">
                {view === 'before' ? <BeforeScreen labels={labels.before} /> : <AfterScreen labels={labels.after} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
